import React, { useState, useEffect, useRef } from "react";
import { FiX } from "react-icons/fi";
import "./Modal.css";
import SideBar from "./modalComponents/Sidebar";
import MainArea from "./modalComponents/MainArea";


export default function Modal({ isOpen, onClose, title, content, matches = [], wordOffsets }) {

  const [editableContent, setEditableContent] = useState(content);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [replaceWord, setReplaceWord] = useState("");
  const [currentActiveWord, setCurrentActiveWord] = useState(null);

  const editableRef = useRef(null);

  // remove active class when modal closes
  useEffect(() => {
    // helper to remove all active markers
    const removeActive = () => {
      document
        .querySelectorAll(".wg-span.active")
        .forEach(activeWord => activeWord.classList.remove("active"));
    };

    // if no active word, just remove and exit
    if (!currentActiveWord) {
      removeActive();
      return;
    }

    // normalize for case-insensitive comparison
    const normalizedActive = currentActiveWord.toLowerCase();

    // remove previous highlights
    removeActive();

    wordOffsets.forEach(item => {
      if (item.word.toLowerCase() === normalizedActive) {
        const activeWord = document.getElementById(item.spanId);
        if (activeWord) activeWord.classList.add("active");
      }
    });

    // cleanup (safety)
    return () => removeActive();
  }, [currentActiveWord, wordOffsets, editableContent]);


  // image to image tag handling
  useEffect(() => {
    // Replace actual <img> tags with their literal HTML
    const processedContent = content.replace(
      /<img([^>]*)>/gi,
      (match) => {
        // escape < and > so it shows as text
        return match
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");
      }
    );
    setEditableContent(processedContent);

    if (editableRef.current) {
      editableRef.current.innerHTML = processedContent;
    }
  }, [content]);

  if (!isOpen) return null;

  // handler for replacing disallowed word in content

  function handleReplaceWord(matchIndex, newWord) {
    if (!newWord?.trim()) return;
    newWord = newWord.toLowerCase();
    const match = matches[matchIndex];
    if (!match) return;

    // Convert allowed alternatives to lowercase array
    const altsArray = normalizeAlts(match.alts).map((alt) => alt.toLowerCase());

    // Case-insensitive comparison
    if (!altsArray.includes(newWord)) {
      alert("Replacement does not match allowed alternatives!");
      setReplaceWord("");
      return;
    }

    // Perform the replacement in content
    const updatedContent = replaceWordInContent(
      editableContent,
      wordOffsets,
      matchIndex,
      newWord
    );

    // Update React state
    setEditableContent(updatedContent);
    setReplaceWord("");
  }

  // alts CSV to string array
  function normalizeAlts(alts) {
    if (Array.isArray(alts)) {
      return alts;
    }
    if (typeof alts === "string") {
      return alts.split(",").map(a => a.trim());
    }
    return [];
  }

  // replace words in content
  function replaceWordInContent(editableContent, wordOffsets, matchIndex, newWord) {
    let updatedContent = editableContent;

    wordOffsets.forEach(item => {
      if (item.matchIndex === matchIndex) {
        const spanEl = document.getElementById(item.spanId);
        let originalText = "";

        // Try to get original text from DOM or HTML
        if (spanEl) {
          originalText = spanEl.innerText.trim();
        } else {
          const match = new RegExp(
            `<span[^>]*id="${item.spanId}"[^>]*>(.*?)</span>`,
            "i"
          ).exec(updatedContent);
          if (match) originalText = match[1];
        }

        // 🔹 Adjust capitalization only for display, not for creating new variants
        let replacedWord = newWord;
        if (originalText) {
          const firstChar = originalText.charAt(0);
          const rest = originalText.slice(1);

          if (originalText === originalText.toUpperCase()) {
            replacedWord = newWord.toUpperCase();
          } else if (
            firstChar === firstChar.toUpperCase() &&
            rest === rest.toLowerCase()
          ) {
            replacedWord =
              newWord.charAt(0).toUpperCase() + newWord.slice(1).toLowerCase();
          } else {
            replacedWord = newWord; // keep as is for mixed or lowercase
          }
        }

        // 🔹 Replace text inside span
        const spanRegex = new RegExp(
          `(<span[^>]*id="${item.spanId}"[^>]*>).*?(</span>)`,
          "g"
        );
        updatedContent = updatedContent.replace(spanRegex, `$1${replacedWord}$2`);

        // 🔹 Update visually in DOM
        if (spanEl) spanEl.innerText = replacedWord;
      }
    });

    return updatedContent;
  }


  // props side bar
  const sidebarProps = {
    currentActiveWord,
    matches,
    currentWordIndex,
    replaceWord,
    setReplaceWord,
    handleReplaceWord,
    editableContent,
    onClose,
    setCurrentActiveWord,
    setCurrentWordIndex
  };

  // props main area
  const mainAreaProps = {
    title,
    editableRef,
    setEditableContent,
    wordOffsets,
    currentActiveWord,
    setCurrentActiveWord,
    setCurrentWordIndex
  };


  function closeButton() {
    return (
      <button
        className="btn-close p-2 rounded-full hover:bg-gray-200 transition"
        onClick={() => {
          onClose();
          setCurrentActiveWord("");
          setCurrentWordIndex(0);
        }}
        aria-label="Close"
      >
        <FiX className="w-5 h-5 text-gray-600" />
      </button>
    );
  }


  // Render modal
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        {closeButton()} {/* X button */}
        <div className="content-wrapper">
          {/* Main area (content) */}
          <MainArea {...mainAreaProps} />

          {/* Sidebar (matches + actions) */}
          <SideBar {...sidebarProps} />
        </div>
      </div>
    </div>
  );

}